/**
 * The screen wake lock — the phone stays awake while it still owes the office
 * something (D-052 §1).
 *
 * ── Grand Dunes, 2026-09-22 ──────────────────────────────────────────────────
 * Chip tapped Submit at 8:29 PM with 105 photos on the phone and left the app
 * open, as he had been told to. 103 pictures reached the office by 9:02 PM.
 * Then the phone auto-locked. iOS stops a web page's JavaScript the moment the
 * screen goes dark, there is no background upload for a home-screen web app,
 * and the sync engine's every trigger (src/main.tsx) runs inside the page — so
 * the last two photos, and the seal waiting behind them (D-050), sat on the
 * phone until he opened the app at 9:46 the next morning. The office read
 * *In progress* all night for a survey he had finished.
 *
 * "Leave the app open" was the right instruction and he followed it. The
 * phone's own auto-lock undid it. This module is the app taking that
 * responsibility off him: while the outbox holds something that can actually
 * be sent, the app asks the browser for a screen wake lock, and the phone does
 * not go dark under a queue.
 *
 * ── What "something that can actually be sent" means ─────────────────────────
 * `shouldHoldScreen` is the whole rule, and it is deliberately narrow:
 *
 *   online      — offline, nothing can move; a lit screen in a pit only spends
 *                 the battery the surveyor will need at the kerb.
 *   linked      — an unlinked device fails every upload (the pill says *Not
 *                 linked*); keeping it awake would burn charge on refusals.
 *   sending > 0 — `queued` minus `blocked`. Parked rows wait for a PERSON, not
 *                 for the radio, and no amount of screen time moves them.
 *
 * The lock is a request, never a demand. A browser without the API, a browser
 * that refuses (low battery, a policy), or a page that is not visible all get
 * the same answer: nothing happens, nothing is logged as an error, and the
 * request is simply made again on the next status. The browser itself releases
 * the lock whenever the page goes hidden, which is why `visibilitychange` is
 * listened to here as well: coming back into view with rows still queued is a
 * reason to ask again.
 *
 * Every browser object is injected (`navigator`, `document`) so the rule and
 * the state machine are proved in the node suite with fakes
 * (tests/unit/wake-lock.test.ts); src/main.tsx passes the real ones.
 */

import type { SyncStatus, SyncStatusListener } from '../db/sync';

/** The part of a `WakeLockSentinel` this module uses. */
export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

/** The part of `navigator` this module uses. `wakeLock` is absent on browsers without the API. */
export interface WakeLockNavigatorLike {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinelLike>;
  };
}

/** The part of `document` this module uses. */
export interface WakeLockDocumentLike {
  visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface WakeLockDeps {
  navigator: WakeLockNavigatorLike;
  document: WakeLockDocumentLike;
  /** Read on every status, not captured: a device linked mid-session starts holding on the next tick. */
  linked: () => boolean;
}

/** The rule. Pure, so the pill's tests and this one agree on what "sending" is. */
export function shouldHoldScreen(status: SyncStatus, linked: boolean): boolean {
  if (!linked || !status.online) return false;
  return status.queued - status.blocked > 0;
}

/**
 * Hold a screen wake lock exactly while `shouldHoldScreen` says so.
 *
 * Returns a detach function that releases any held lock and stops listening.
 * On a browser without `navigator.wakeLock` it attaches nothing and returns
 * a no-op.
 */
export function attachWakeLock(
  sync: { subscribe(listener: SyncStatusListener): () => void },
  deps: WakeLockDeps,
): () => void {
  const api = deps.navigator.wakeLock;
  if (!api) return () => undefined;

  let wanted = false;
  let detached = false;
  let held: WakeLockSentinelLike | null = null;
  /** The request in flight, so two statuses in one tick make one request. */
  let requesting: Promise<void> | null = null;

  const release = (): void => {
    const sentinel = held;
    held = null;
    if (sentinel) void sentinel.release().catch(() => undefined);
  };

  const acquire = (): void => {
    if (held || requesting) return;
    // A request made while hidden is refused by every browser; wait for
    // `visibilitychange` to bring the page back instead.
    if (deps.document.visibilityState !== 'visible') return;
    requesting = api
      .request('screen')
      .then((sentinel) => {
        // The queue emptied, or the app detached, while the browser was
        // deciding — a lock nobody wants is let go at once.
        if (!wanted || detached) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        held = sentinel;
        sentinel.addEventListener('release', () => {
          // The browser let go on its own (the page went hidden). Forget it,
          // so the next visible status asks again.
          if (held === sentinel) held = null;
        });
      })
      // A refusal is not an error: the next status simply asks again.
      .catch(() => undefined)
      .finally(() => {
        requesting = null;
      });
  };

  const reconcile = (): void => {
    if (wanted) acquire();
    else release();
  };

  const unsubscribe = sync.subscribe((status) => {
    wanted = shouldHoldScreen(status, deps.linked());
    reconcile();
  });
  const onVisibility = (): void => reconcile();
  deps.document.addEventListener('visibilitychange', onVisibility);

  return () => {
    detached = true;
    wanted = false;
    unsubscribe();
    deps.document.removeEventListener('visibilitychange', onVisibility);
    release();
  };
}
